import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CatalogSimulationPanel } from '@components/portfolio/CatalogSimulationPanel';
import { DashboardLayout } from '@components/layout/DashboardLayout';
import { Spinner } from '@components/common/Spinner';
import { PropertySummary } from '@components/analysis/PropertySummary';
import { FinancialSummary } from '@components/analysis/FinancialSummary';
import { RentalYieldAnalysis } from '@components/analysis/RentalYieldAnalysis';
import { FinancingStructure } from '@components/analysis/FinancingStructure';
import { NotesTab } from '@components/analysis/NotesTab';
import { PpeAnalysisTab } from '@components/analysis/PpeAnalysisTab';
import { RegblTab } from '@components/analysis/RegblTab';
import { LocationTab } from '@components/analysis/LocationTab';
import { OfferLetterModal } from '@components/analysis/OfferLetterModal';
import { AgenceTab } from '@components/analysis/AgenceTab';
import { FlipAnalysisTab } from '@components/analysis/FlipAnalysisTab';
import { PropertyFormWizard } from '@components/properties/PropertyFormWizard';
import { Modal } from '@components/feedback/Modal';
import { propertyService } from '@services/property.service';
import { documentService } from '@services/document.service';
import { fileService } from '@services/file-service';
import { PropertyAnalysis, OffreData, UploadedFile } from '../types/index';
import { calculerOffreData } from '@utils/offre-calcul';
import { track } from '@services/analytics';
import { PlanGate } from '@components/PlanGate';

type Tab = 'analyse' | 'notes' | 'ppe-analyse' | 'regbl' | 'localisation' | 'agence' | 'simulations' | 'flip';

export const Analysis: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<PropertyAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>(
    (location.state as { openTab?: Tab } | null)?.openTab ?? 'analyse'
  );
  const [tauxCapitalisation, setTauxCapitalisation] = useState<number>(5);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [checkingOffline, setCheckingOffline] = useState(false);
  const [offlineCheckResult, setOfflineCheckResult] = useState<string | null>(null);
  const [generatingDossier, setGeneratingDossier] = useState(false);
  const [generatedDocs, setGeneratedDocs] = useState<UploadedFile[]>([]);

  const loadGeneratedDocs = async () => {
    if (!id) return;
    try {
      const files = await fileService.listPropertyFiles(id);
      setGeneratedDocs(files.filter(f => f.mimeType.includes('wordprocessingml')));
    } catch {}
  };

  useEffect(() => {
    const loadAnalysis = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        setError(null);
        const [result] = await Promise.all([
          propertyService.getAnalysis(id),
          loadGeneratedDocs(),
        ]);
        setData(result);
        setTauxCapitalisation(
          (result.property as any).tauxCapitalisation
          ?? result.analysis.tauxCapitalisationDefaut
          ?? 5
        );
      } catch (err) {
        console.error('Erreur chargement analyse:', err);
        setError('Impossible de charger les donn\u00e9es d\u2019analyse.');
      } finally {
        setIsLoading(false);
      }
    };
    loadAnalysis();
  }, [id]);

  // Cleanup du debounce à la destruction du composant
  useEffect(() => () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); }, []);

  // Scroll vers l'onglet actif quand il change (tab bar mobile)
  useEffect(() => {
    const activeEl = tabsRef.current?.querySelector('[data-active="true"]');
    activeEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeTab]);

  // Calcul de toutes les valeurs basées sur l'offre conseillée (pilotées par le slider)
  const offreData = useMemo<OffreData | null>(() => {
    if (!data) return null;
    return calculerOffreData(data.analysis, data.settings, data.property, tauxCapitalisation);
  }, [tauxCapitalisation, data]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <p className="text-red-500 dark:text-red-400">{error || 'Donn\u00e9es introuvables'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Retour
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const { property, analysis } = data;

  const revenuNet = analysis.loyerAnnuel - analysis.chargesPPEAnnuelles
    - analysis.coutsMaintenance - analysis.impotFoncier;

  const handleTauxChange = (newTaux: number) => {
    setTauxCapitalisation(newTaux);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      if (id) propertyService.updateSelection(id, { tauxCapitalisation: newTaux });
    }, 1500);
  };

  const handlePrixConseilleChange = (prix: number) => {
    if (prix > 0 && revenuNet > 0) {
      const newTaux = (revenuNet / prix) * 100;
      handleTauxChange(Math.max(1, Math.min(15, newTaux)));
    }
  };

  const tabs: { key: Tab; label: string; shortLabel: string; disabled?: boolean }[] = [
    { key: 'analyse',      label: 'Analyse',                 shortLabel: 'Analyse'    },
    { key: 'simulations',  label: '🎯 Scénarios',            shortLabel: '🎯 Scén.'   },
    { key: 'flip',         label: '🔨 Flip',                 shortLabel: '🔨 Flip'    },
    { key: 'ppe-analyse',  label: 'Documents PPE - Analyse', shortLabel: 'PPE'        },
    { key: 'regbl',        label: 'Information RegBL',       shortLabel: 'RegBL',     disabled: !property.egid },
    { key: 'localisation', label: 'Localisation',            shortLabel: 'Localisa.', disabled: !property.latitude || !property.longitude },
    { key: 'notes',        label: 'Notes',                   shortLabel: 'Notes'      },
    { key: 'agence',       label: 'Agence',                  shortLabel: 'Agence'     },
  ];

  const handleCheckOffline = async () => {
    if (!id) return;
    setCheckingOffline(true);
    setOfflineCheckResult(null);
    try {
      const result = await propertyService.checkOffline(id);
      if (result.markedWithdrawn) {
        setOfflineCheckResult('withdrawn');
        const updated = await propertyService.getAnalysis(id);
        setData(updated);
      } else if (result.available === false) {
        setOfflineCheckResult('already_withdrawn');
      } else if (result.available === true) {
        setOfflineCheckResult('available');
      } else {
        setOfflineCheckResult('error');
      }
    } catch {
      setOfflineCheckResult('error');
    } finally {
      setCheckingOffline(false);
    }
  };

  const handleGenerateDossier = async () => {
    if (!id) return;
    setGeneratingDossier(true);
    try {
      const result = await documentService.generateBankDossier(id, tauxCapitalisation);
      const apiBase = (import.meta.env.VITE_API_URL as string) || '/api';
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/files/${result.fileId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Erreur génération dossier:', e);
    } finally {
      setGeneratingDossier(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-200 truncate">
              Analyse - {property.address?.match(/\d/) ? `${property.address}, ` : ''}{property.city}
            </h1>
          </div>
          <div className="flex items-center flex-wrap gap-2 pl-11 sm:pl-0">
            <button
              onClick={() => setEditModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg shadow-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="hidden sm:inline">Modifier</span>
            </button>

            {/* Badge retiré ou bouton vérification */}
            {property.offMarketAt ? (
              <div className="flex items-center gap-1.5">
                <span className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  Retiré le {new Date(property.offMarketAt).toLocaleDateString('fr-CH')}
                </span>
                <button
                  onClick={async () => {
                    if (!id) return;
                    await propertyService.update(id, { isOffMarket: false } as any);
                    setData(d => d ? { ...d, property: { ...d.property, isOffMarket: false, offMarketAt: null } } : d);
                  }}
                  title="Remettre l'annonce en ligne"
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="hidden sm:inline">Remettre en ligne</span>
                </button>
              </div>
            ) : property.sourceUrl ? (
              <button
                onClick={handleCheckOffline}
                disabled={checkingOffline}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 rounded-lg shadow-sm transition-colors"
              >
                {checkingOffline ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Vérification…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="hidden sm:inline">Vérifier disponibilité</span>
                    <span className="sm:hidden">Vérifier</span>
                  </>
                )}
              </button>
            ) : null}

            {/* Résultat de la vérification */}
            {offlineCheckResult === 'available' && (
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">✓ Disponible</span>
            )}
            {offlineCheckResult === 'error' && (
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">⚠ Vérification impossible</span>
            )}
            <PlanGate minRole="PRO">
              <button
                onClick={handleGenerateDossier}
                disabled={generatingDossier}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg shadow-sm transition-colors"
              >
                {generatingDossier ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Génération…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="hidden sm:inline">Dossier banquier PDF</span>
                    <span className="sm:hidden">PDF</span>
                  </>
                )}
              </button>
            </PlanGate>
            <PlanGate minRole="PRO">
              <button
                onClick={() => setOfferModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Générer une offre d'achat</span>
                <span className="sm:hidden">Offre</span>
              </button>
            </PlanGate>
          </div>
        </div>

        {/* Offres d'achat générées */}
        {generatedDocs.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2">
            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
              Offres générées :
            </span>
            {generatedDocs.map(doc => (
              <div key={doc.id} className="flex items-center">
                <button
                  onClick={async () => {
                    try {
                      const apiBase = (import.meta.env.VITE_API_URL as string) || '/api';
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${apiBase}/files/${doc.id}/download`, {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                      });
                      if (!res.ok) throw new Error(`HTTP ${res.status}`);
                      const blob = await res.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = blobUrl;
                      a.download = doc.filename;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(blobUrl);
                    } catch (e) {
                      console.error('Erreur téléchargement:', e);
                    }
                  }}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-blue-700 dark:text-blue-300 bg-white dark:bg-blue-900/40 border border-blue-200 dark:border-blue-700 rounded-l hover:bg-blue-100 dark:hover:bg-blue-800/60 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Offre du {new Date(doc.createdAt).toLocaleDateString('fr-CH')}
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('Supprimer cette offre ?')) return;
                    try {
                      await fileService.deleteFile(doc.id);
                      await loadGeneratedDocs();
                    } catch (e) {
                      console.error('Erreur suppression:', e);
                    }
                  }}
                  title="Supprimer"
                  className="flex items-center justify-center px-1.5 py-1 text-blue-500 dark:text-blue-400 bg-white dark:bg-blue-900/40 border border-l-0 border-blue-200 dark:border-blue-700 rounded-r hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="relative border-b border-gray-200 dark:border-gray-700">
          {/* Dégradé droit — indique qu'il y a d'autres onglets à scroller (mobile) */}
          <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white dark:from-gray-900 to-transparent z-10 sm:hidden" />
          <div ref={tabsRef} className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                data-active={activeTab === tab.key}
                onClick={() => { if (!tab.disabled) { setActiveTab(tab.key); track('tab-changed', { tab: tab.key }); } }}
                disabled={tab.disabled}
                className={`flex-shrink-0 whitespace-nowrap px-3 py-2 sm:px-4 text-sm font-medium transition-colors
                  ${activeTab === tab.key
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : tab.disabled
                      ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer'
                  }`}
              >
                <span className="sm:hidden">{tab.shortLabel}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'analyse' && offreData && (
          <PlanGate minRole="PREMIUM">
            <>
              <PropertySummary property={property} />
              <FinancialSummary
                analysis={analysis}
                offreData={offreData}
                tauxCapitalisation={tauxCapitalisation}
                onTauxCapitalisationChange={handleTauxChange}
                onPrixConseilleChange={handlePrixConseilleChange}
              />
              <RentalYieldAnalysis
                analysis={analysis}
                offreData={offreData}
                rentPrice={property.rentPrice || property.estimatedRent || 0}
                isRentEstimated={!property.rentPrice && !!property.estimatedRent}
                estimatedRent={property.estimatedRent ?? undefined}
                estimatedYield={property.estimatedYield ?? undefined}
                rentConfidence={property.rentConfidence ?? undefined}
                mortgageRateStressTest={data.settings.mortgageRateStressTest}
                roeTarget={data.settings.roeTarget}
                askingPrice={property.askingPrice ?? undefined}
                estimatedSalePrice={property.estimatedSalePrice ?? undefined}
                estimatedSalePriceMin={property.estimatedSalePriceMin ?? undefined}
                estimatedSalePriceMax={property.estimatedSalePriceMax ?? undefined}
                saleEstimateSource={property.saleEstimateSource ?? undefined}
                area={property.area}
                propertyId={property.id}
                address={property.address}
                city={property.city}
                postalCode={property.postalCode}
                latitude={property.latitude ?? undefined}
                longitude={property.longitude ?? undefined}
              />
              <FinancingStructure
                analysis={analysis}
                offreData={offreData}
              />
            </>
          </PlanGate>
        )}

        {activeTab === 'ppe-analyse' && id && (
          <PlanGate minRole="PRO">
            <PpeAnalysisTab propertyId={id} />
          </PlanGate>
        )}

        {activeTab === 'notes' && id && (
          <NotesTab propertyId={id} />
        )}

        {activeTab === 'regbl' && property.egid && (
          <PlanGate minRole="PREMIUM">
            <RegblTab egid={property.egid} />
          </PlanGate>
        )}

        {activeTab === 'localisation' && property.latitude != null && property.longitude != null && (
          <PlanGate minRole="PREMIUM">
            <LocationTab lat={property.latitude} lng={property.longitude} propertyId={property.id} commune={property.commune} rooms={property.rooms} />
          </PlanGate>
        )}

        {activeTab === 'agence' && (
          <AgenceTab
            property={property}
            onUpdate={(updated) => setData(prev => prev ? { ...prev, property: { ...prev.property, ...updated } } : prev)}
          />
        )}

        {activeTab === 'simulations' && (
          <CatalogSimulationPanel property={property} />
        )}

        {activeTab === 'flip' && (
          <PlanGate minRole="PREMIUM">
            <FlipAnalysisTab property={property} />
          </PlanGate>
        )}
      </div>

      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Modifier l'annonce"
        size="xl"
      >
        <PropertyFormWizard
          property={property}
          onSuccess={async () => {
            setEditModalOpen(false);
            const result = await propertyService.getAnalysis(id!);
            setData(result);
          }}
          onCancel={() => setEditModalOpen(false)}
          onSave={async () => {
            const result = await propertyService.getAnalysis(id!);
            setData(result);
          }}
        />
      </Modal>

      {offerModalOpen && id && (
        <OfferLetterModal
          propertyId={id}
          askingPrice={property.askingPrice ?? undefined}
          offreConseillee={offreData?.offreConseillee}
          agencyName={(property as any).agencyName ?? undefined}
          onClose={() => { setOfferModalOpen(false); loadGeneratedDocs(); }}
        />
      )}
    </DashboardLayout>
  );
};
